import { useEffect, useRef } from 'react'
import {
  BadgeCheck,
  CheckCircle2,
  CircleCheck,
  Hammer,
  ImageIcon,
  Inbox,
  Loader2,
  MessageCircle,
  Pencil,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import {
  useAdminInfiniteQuotes,
  useAdminQuoteStatusCount,
  type AdminQuoteListFilters,
} from 'src/api/db/apiQuote'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import {
  BUILDING_QUOTE_STATUS,
  SHIPPING_QUOTE_STATUS,
} from 'src/shared/consts/quoteStatus'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { PipelineCard } from './PipelineCard'

const COLUMN_PAGE_SIZE = 20

const PIPELINE_TONE: Record<string, { icon: string; count: string }> = {
  '0_building_quote': {
    icon: 'bg-slate-200 text-slate-700',
    count: 'bg-slate-700 text-white',
  },
  '1_contacted_sales': {
    icon: 'bg-amber-100 text-amber-800',
    count: 'bg-amber-500 text-white',
  },
  '1_5_sales_approved_quote': {
    icon: 'bg-orange-100 text-orange-800',
    count: 'bg-orange-500 text-white',
  },
  '2_approved_quote': {
    icon: 'bg-rose-100 text-rose-800',
    count: 'bg-rose-500 text-white',
  },
  '3_approved_proofs': {
    icon: 'bg-sky-100 text-sky-800',
    count: 'bg-sky-600 text-white',
  },
  '4_started_production': {
    icon: 'bg-violet-100 text-violet-800',
    count: 'bg-violet-600 text-white',
  },
  '5_awaiting_shipment': {
    icon: 'bg-teal-100 text-teal-800',
    count: 'bg-teal-600 text-white',
  },
  '8_delivered': {
    icon: 'bg-emerald-100 text-emerald-800',
    count: 'bg-emerald-600 text-white',
  },
}

const STATUS_ICON: Record<string, LucideIcon> = {
  '0_building_quote': Pencil,
  '1_contacted_sales': MessageCircle,
  '1_5_sales_approved_quote': CircleCheck,
  '2_approved_quote': ImageIcon,
  '3_approved_proofs': BadgeCheck,
  '4_started_production': Hammer,
  '5_awaiting_shipment': Truck,
  '8_delivered': CheckCircle2,
}

export const PipelineColumn = ({
  status,
  label,
  filters,
}: {
  status: string
  label: string
  filters: Omit<AdminQuoteListFilters, 'statuses' | 'includeNullStatus'>
}) => {
  const { t } = useLocalization()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const includeNullStatus = status === BUILDING_QUOTE_STATUS
  // Include legacy quote-level shipping alias in the Shipping column
  const statuses =
    status === SHIPPING_QUOTE_STATUS
      ? [SHIPPING_QUOTE_STATUS, 'awaiting_shipment']
      : [status]

  const {
    data,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useAdminInfiniteQuotes({
    pageSize: COLUMN_PAGE_SIZE,
    statuses,
    includeNullStatus,
    ...filters,
  })

  const { data: totalCount } = useAdminQuoteStatusCount({
    statuses,
    includeNullStatus,
    ...filters,
  })

  const quotes = data?.pages.flatMap((page) => page.data) ?? []
  const badgeCount = totalCount ?? quotes.length
  const tone = PIPELINE_TONE[status] ?? PIPELINE_TONE['0_building_quote']
  const Icon = STATUS_ICON[status] ?? Inbox

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { root: node.parentElement, rootMargin: '120px', threshold: 0 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <Column className="w-[272px] shrink-0 gap-2.5">
      <Row className="items-center gap-2 px-0.5">
        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            tone.icon
          )}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2} />
        </div>
        <p className="min-w-0 flex-1 truncate text-sm font-medium">
          {t(label)}
        </p>
        <span
          className={cn(
            'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
            tone.count
          )}
        >
          {badgeCount}
        </span>
      </Row>

      <Column className="max-h-[70vh] min-h-[140px] gap-2 overflow-y-auto rounded-xl bg-muted/50 p-2">
        {isPending ? (
          <Column className="items-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </Column>
        ) : quotes.length === 0 ? (
          <Column className="flex-1 items-center justify-center gap-1.5 py-10">
            <Inbox className="h-4 w-4 text-muted-foreground/70" />
            <p className="text-xs text-muted-foreground">
              {t('Nothing here', 'Inget här')}
            </p>
          </Column>
        ) : (
          quotes.map((quote) => (
            <PipelineCard
              key={quote.id}
              quote={quote}
              placeholderClassName={tone.icon}
            />
          ))
        )}

        <div ref={sentinelRef} className="h-1 w-full shrink-0" />

        {isFetchingNextPage && (
          <Column className="items-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </Column>
        )}
      </Column>
    </Column>
  )
}
